import { booleanAttribute, ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { LibSharedCommonModule } from 'projects/lib-shared-common/src/public-api';
import { mergeClasses } from '@solvace/zard/shared/utils/merge-classes';

export interface SvzTabNavItem {
  key: string;
  label: string;
  disabled?: boolean;
}

const LIST_CLASSES =
  'flex gap-6 border-b border-input mb-2 ' +
  'max-[640px]:flex-nowrap max-[640px]:overflow-x-auto max-[640px]:overflow-y-hidden max-[640px]:-mx-3.5 max-[640px]:px-3.5 ' +
  'max-[640px]:[-webkit-overflow-scrolling:touch] max-[640px]:[scrollbar-width:none] max-[640px]:[&::-webkit-scrollbar]:hidden';

const SCROLL_AT_EVERY_WIDTH_LIST_CLASSES =
  'flex-nowrap overflow-x-auto overflow-y-hidden [-webkit-overflow-scrolling:touch] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden';

const SCROLL_AT_EVERY_WIDTH_TAB_CLASSES = 'flex-none whitespace-nowrap';

export type SvzTabNavVariant = 'default' | 'analytics' | 'analytics-inline' | 'analytics-compact';

/**
 * Prototype `.an-tabs` (gap 28px, margin-bottom 18px; on phone the generic `.tabs` rule wins: gap 24px, margin-bottom 8px)
 * and `.an-tabs-inline` (margin-bottom 10px). `analytics-compact` is the Action Plan `#screen-an .an-tabs`, whose id-scoped
 * rule outranks the phone `.tabs` rule: gap 16px below 1024px, margin-bottom 18px at every width.
 */
const VARIANT_LIST_CLASSES: Record<SvzTabNavVariant, string> = {
  default: '',
  analytics: 'gap-7 mb-[18px] max-[640px]:mb-2 max-[640px]:gap-6',
  'analytics-inline': 'gap-7 mb-[10px] max-[640px]:mb-2 max-[640px]:gap-6',
  'analytics-compact': 'gap-7 mb-[18px] max-[1023px]:gap-4',
};

const TAB_CLASSES =
  'relative cursor-pointer border-0 bg-transparent px-0 py-2.5 text-sm [font-family:inherit] max-[640px]:flex-none ' +
  'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary ' +
  'disabled:pointer-events-none disabled:opacity-50';

const ACTIVE_TAB_CLASSES = "font-semibold text-primary after:absolute after:inset-x-0 after:-bottom-px after:h-0.5 after:rounded-[2px] after:bg-primary after:content-['']";

const INACTIVE_TAB_CLASSES = 'text-[#5a5f6b]';
const INACTIVE_TAB_HOVER_CLASSES = 'transition-colors hover:text-primary';

@Component({
  selector: 'svz-tab-nav',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './svz-tab-nav.component.html',
  imports: [LibSharedCommonModule],
  host: {
    class: 'block',
  },
})
export class SvzTabNavComponent {
  readonly items = input.required<SvzTabNavItem[]>();

  readonly activeKey = input<string | null>(null);

  readonly ariaLabel = input<string | null>(null);

  /** Screen prefix for every `svcTestId` this component renders (`<prefix>__tab--<key>`). */
  readonly testIdPrefix = input('svz-tab-nav');

  readonly hoverHighlight = input(false, { transform: booleanAttribute });

  readonly lineHeight = input<string | null>(null);

  readonly scrollAtEveryWidth = input(false, { transform: booleanAttribute });

  readonly variant = input<SvzTabNavVariant>('default');

  readonly tabSelected = output<SvzTabNavItem>({ alias: 'onTabSelected' });

  protected readonly listClasses = computed(() =>
    mergeClasses(LIST_CLASSES, VARIANT_LIST_CLASSES[this.variant()], this.scrollAtEveryWidth() ? SCROLL_AT_EVERY_WIDTH_LIST_CLASSES : '')
  );

  private readonly widthClasses = computed(() => (this.scrollAtEveryWidth() ? SCROLL_AT_EVERY_WIDTH_TAB_CLASSES : ''));

  protected isActive(item: SvzTabNavItem): boolean {
    return item.key === this.activeKey();
  }

  protected tabClasses(item: SvzTabNavItem): string {
    if (this.isActive(item)) {
      return mergeClasses(TAB_CLASSES, this.widthClasses(), ACTIVE_TAB_CLASSES);
    }
    return mergeClasses(TAB_CLASSES, this.widthClasses(), INACTIVE_TAB_CLASSES, this.hoverHighlight() ? INACTIVE_TAB_HOVER_CLASSES : '');
  }

  protected onSelect(item: SvzTabNavItem): void {
    if (item.disabled || this.isActive(item)) {
      return;
    }
    this.tabSelected.emit(item);
  }
}
